/**
 * DesktopNav — dropdown menu for desktop (hidden on mobile where MobileNav handles it).
 *
 * Renders a hamburger button that opens a dropdown with module links.
 * Used on the home page (replacing ~/home label) and in SiteHeader
 * (left of the wordmark).
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext.jsx';
import './DesktopNav.css';


const NAV_ITEMS = [
  { to: '/',       label: 'Home' },
  { to: '/tracks', label: 'Tracks' },
  { to: '/quotes', label: 'Quotes' },
  { to: '/funny',  label: 'Funny Page' },
  { href: 'https://photos.nachofiles.com',    label: 'Photos',    external: true },
  { href: 'https://nextcloud.nachofiles.com',  label: 'Nextcloud', external: true },
  { href: 'https://immich.nachofiles.com',     label: 'Immich',    external: true },
];


export default function DesktopNav() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Close on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function onDocClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    function onKey(e) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="dnav-wrap" ref={wrapRef}>
      <button
        className={`dnav-toggle ${open ? 'open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-label="Navigation menu"
        aria-expanded={open}
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="currentColor">
          <rect y="3" width="18" height="2" rx="1" />
          <rect y="8" width="18" height="2" rx="1" />
          <rect y="13" width="18" height="2" rx="1" />
        </svg>
      </button>

      {open && (
        <div className="dnav-dropdown">
          {NAV_ITEMS.map((item) =>
            item.external ? (
              <a
                key={item.href}
                href={item.href}
                className="dnav-item"
                target="_blank"
                rel="noopener"
              >
                <span>{item.label}</span>
                <span className="dnav-external">↗</span>
              </a>
            ) : (
              <Link
                key={item.to}
                to={item.to}
                className={`dnav-item ${location.pathname === item.to ? 'active' : ''}`}
              >
                {item.label}
              </Link>
            )
          )}

          {user?.role === 'admin' && (
            <>
              <div className="dnav-divider" />
              <Link to="/admin" className="dnav-item dnav-admin">Admin</Link>
            </>
          )}
        </div>
      )}
    </div>
  );
}
